import { Button } from '@roger/ui-components';
import { CircleAlert, ExternalLink } from 'lucide-react';
import React, { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { PermissionSlug } from '../../constants/featureFlags';
import { completeEnrollmentTask } from '../../services/clinicsApi';
import { useStore } from '../../store';
import { EnrollmentTaskForm } from './EnrollmentTaskForm';
import {
  PAYER_TRANSACTION_LABELS,
  linkName,
  type EnrollmentTodo,
  type TodoStep,
} from './payerEnrollments';

interface PayerStepsProps {
  payerKey: string;
  /** The jobs with a step for this payer; a shared one covers others too. */
  todos: EnrollmentTodo[];
  /** Reload the tab after a step is done, so it leaves the list. */
  onChanged?: () => void;
}

// "[label](https://…)" as Stedi writes a link inside a step's text.
const MARKDOWN_LINK = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

/** A step's text with its markdown links as real links, where they stand. */
function WithLinks({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(MARKDOWN_LINK)) {
    const start = match.index ?? 0;
    parts.push(text.slice(last, start));
    parts.push(
      <a
        key={start}
        href={match[2]}
        target='_blank'
        rel='noopener noreferrer'
        className='text-primary underline underline-offset-2'
      >
        {match[1]}
      </a>
    );
    last = start + match[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

/**
 * What the agency has to do for one payer: per job, the payer's reason if it
 * rejected, the steps and their pages, and the way to finish it — a Done per
 * clinic, or the answer box for a step that asks for a value. A job shared
 * with other payers says so, and Done clears it for all of them.
 */
export function PayerSteps({ payerKey, todos, onChanged }: PayerStepsProps) {
  return (
    <div className='space-y-8'>
      {sameSteps(todos).map(group => (
        <div key={group[0].key}>
          <Steps
            group={group}
            payerKey={payerKey}
            onDone={() => onChanged?.()}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Jobs that ask the same thing, read once: a step that asks for a value is its
 * own job per clinic, and five clinics would otherwise be five copies of the
 * same text. A rejection keeps its own, since its reason is the clinic's.
 */
const sameSteps = (todos: EnrollmentTodo[]): EnrollmentTodo[][] => {
  const groups = new Map<string, EnrollmentTodo[]>();
  for (const todo of todos) {
    const [{ row, task, transaction }] = todo.steps;
    const key =
      row.status === 'rejected' || !task
        ? todo.key
        : [
            transaction,
            task.instructions ?? '',
            ...(task.field_labels ?? []),
            ...todo.links.map(link => link.url),
          ].join('|');
    groups.set(key, [...(groups.get(key) ?? []), todo]);
  }
  return [...groups.values()];
};

function Steps({
  group,
  payerKey,
  onDone,
}: {
  group: EnrollmentTodo[];
  payerKey: string;
  onDone: () => void;
}) {
  const [todo] = group;
  const allSteps = group.flatMap(item => item.steps);
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const [first] = todo.steps;
  const { row, task } = first;
  const isRejection = row.status === 'rejected';
  const links = todo.links.length > 0 ? todo.links : (task?.links ?? []);
  // A page the text already links to, where it says so, needs no button too.
  const linkedInText = new Set(
    [...(task?.instructions ?? '').matchAll(MARKDOWN_LINK)].map(
      match => match[2]
    )
  );
  const buttonLinks = links.filter(link => !linkedInText.has(link.url));
  const otherPayers = [
    ...new Set(
      allSteps
        .filter(step => step.payerKey !== payerKey)
        .map(step => step.payerName)
    ),
  ];
  const fields = task?.fields ?? [];
  const labels = task?.field_labels ?? [];
  const remaining = (row.tasks ?? []).filter(t => !t.is_complete).length - 1;
  const mayAnswer = hasPermission(PermissionSlug.ENROLLMENT_SUBMIT);
  // The boxes name what they ask for, so "You'll need" would repeat them.
  const answersHere =
    mayAnswer && fields.length > 0 && Boolean(task?.answerable);
  const canMarkAllDone =
    mayAnswer &&
    fields.length === 0 &&
    allSteps.every(
      step => step.task?.answerable && step.task.task_id && step.row.record_id
    );

  return (
    <>
      <h3 className='flex items-center gap-2 border-b pb-1.5 text-base font-semibold'>
        {/* The table's Needs action mark, so the two read as one. */}
        <CircleAlert className='h-5 w-5 shrink-0 text-red-600' />
        <span>
          {PAYER_TRANSACTION_LABELS[first.transaction]} Enrollment
          {isRejection && (
            <span className='font-medium text-red-600'> · Rejected</span>
          )}
        </span>
      </h3>

      <div className='mt-3 space-y-4 text-sm leading-relaxed'>
        {isRejection && (
          <section className='space-y-2'>
            <h3 className='font-medium'>Why it was rejected</h3>
            {row.reason ? (
              <p className='whitespace-pre-line'>{row.reason}</p>
            ) : (
              // Either the payer gave no reason, or the one it gave cannot be
              // shown as written. Both leave the reader in the same place.
              <p className='text-muted-foreground'>
                No reason we can show here. Your Roger contact can find out why.
              </p>
            )}
          </section>
        )}

        {(!isRejection || task) && (
          <section className='space-y-2'>
            {/* The payer's pages beside the text they go with. */}
            <div className='flex items-start gap-3'>
              <div className='min-w-0 flex-1'>
                {task?.instructions ? (
                  // The longer instructions arrive as several paragraphs; each is
                  // its own, a little apart, rather than a full blank line.
                  <div className='space-y-1.5'>
                    {task.instructions.split(/\n\s*\n/).map(paragraph => (
                      <p key={paragraph} className='whitespace-pre-line'>
                        <WithLinks text={paragraph.trim()} />
                      </p>
                    ))}
                  </div>
                ) : links.length > 0 ? (
                  <p>Follow the payer&apos;s enrollment steps.</p>
                ) : (
                  // A draft, or a step with no text: say one exists rather than
                  // showing nothing.
                  <p className='text-muted-foreground'>
                    This enrollment is waiting on a step from your agency. Your
                    Roger contact has the details.
                  </p>
                )}
              </div>
              {buttonLinks.length > 0 && (
                <div className='flex shrink-0 flex-col items-end gap-2'>
                  {buttonLinks.map(link => (
                    <Button key={link.url} variant='outline' size='sm' asChild>
                      <a
                        href={link.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {linkName(link)} instructions
                        <ExternalLink className='ml-1.5 h-3.5 w-3.5' />
                      </a>
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {labels.length > 0 && !answersHere && (
              <p className='text-muted-foreground'>
                You&apos;ll need: {labels.join(', ')}
              </p>
            )}
          </section>
        )}

        {otherPayers.length > 0 && (
          <p className='text-muted-foreground'>
            Also covers {otherPayers.join(', ')}. Marking it done here clears it
            there too.
          </p>
        )}

        {task && (
          // A ruled line per clinic, like a form.
          <section>
            <div className='divide-y border-t'>
              {fields.length > 0 ? (
                // The answer differs per clinic, so each gets its own box.
                group.map(item => {
                  const [{ row: itemRow, task: itemTask, clinicName }] =
                    item.steps;
                  return (
                    <div key={item.key} className='py-2'>
                      {itemTask?.answerable &&
                      itemRow.record_id &&
                      mayAnswer ? (
                        <EnrollmentTaskForm
                          recordId={itemRow.record_id}
                          task={itemTask}
                          onSent={onDone}
                          label={clinicName}
                        />
                      ) : (
                        // Bank or tax detail, a step behind another, or a
                        // person without the permission to send it.
                        <p>
                          {clinicName}
                          {' · '}
                          <span className='text-muted-foreground'>
                            Contact Roger to complete this step.
                          </span>
                        </p>
                      )}
                    </div>
                  );
                })
              ) : canMarkAllDone ? (
                <ClinicsDone
                  todo={{ ...todo, steps: allSteps }}
                  onDone={onDone}
                />
              ) : (
                <>
                  <ClinicList todo={{ ...todo, steps: allSteps }} />
                  <p className='py-2 text-muted-foreground'>
                    Contact Roger to complete this step.
                  </p>
                </>
              )}
            </div>
            {allSteps.length === 1 && remaining > 0 && (
              <p className='border-t py-2 text-xs text-muted-foreground'>
                {remaining} more step{remaining === 1 ? '' : 's'} follow
                {remaining === 1 ? 's' : ''} this one.
              </p>
            )}
          </section>
        )}
      </div>
    </>
  );
}

/** The job's steps by clinic, each clinic's steps across every payer. */
const byClinic = (todo: EnrollmentTodo) => {
  const clinics = new Map<string, { name: string; steps: TodoStep[] }>();
  for (const step of todo.steps) {
    const id = step.row.agency_clinic_id;
    const clinic = clinics.get(id) ?? { name: step.clinicName, steps: [] };
    clinic.steps.push(step);
    clinics.set(id, clinic);
  }
  return [...clinics.entries()].map(([id, clinic]) => ({ id, ...clinic }));
};

/** Who a step is for, when the agency cannot mark it done from here. */
function ClinicList({ todo }: { todo: EnrollmentTodo }) {
  return (
    <>
      {byClinic(todo).map(clinic => (
        <div key={clinic.id} className='py-2'>
          {clinic.name}
        </div>
      ))}
    </>
  );
}

/**
 * A Done per clinic, since a job for several clinics rarely gets finished in
 * one sitting, and Mark all done for a page that covers them all at once.
 */
function ClinicsDone({
  todo,
  onDone,
}: {
  todo: EnrollmentTodo;
  onDone: () => void;
}) {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const [sending, setSending] = useState<string | null>(null);
  // Which button is asking "sure?": a click can't be taken back, and a shared
  // step clears for every payer it covers.
  const [confirming, setConfirming] = useState<string | null>(null);
  const clinics = byClinic(todo);

  const markDone = async (key: string, steps: TodoStep[]) => {
    setSending(key);
    const results = await Promise.allSettled(
      steps.map(step =>
        completeEnrollmentTask(
          step.row.record_id as string,
          step.task?.task_id as string,
          {}
        )
      )
    );
    const failed = results.filter(result => result.status === 'rejected');
    failed.forEach(result =>
      console.error('Failed to mark enrollment step done', result)
    );
    if (failed.length === 0) {
      showSuccess('Marked as done.');
    } else {
      showError(
        `Couldn't mark ${failed.length} of ${results.length} as done. Please try again.`
      );
    }
    setSending(null);
    setConfirming(null);
    onDone();
  };

  const action = (
    key: string,
    steps: TodoStep[],
    label: string,
    variant?: 'outline'
  ) =>
    confirming === key ? (
      <>
        <Button
          size='sm'
          disabled={sending !== null}
          onClick={() => void markDone(key, steps)}
        >
          {sending === key ? 'Sending…' : 'Confirm'}
        </Button>
        <Button
          size='sm'
          variant='outline'
          disabled={sending !== null}
          onClick={() => setConfirming(null)}
        >
          Cancel
        </Button>
      </>
    ) : (
      <Button
        size='sm'
        variant={variant}
        disabled={sending !== null}
        onClick={() => setConfirming(key)}
      >
        {label}
      </Button>
    );

  return (
    <>
      {clinics.map(clinic => (
        <div key={clinic.id} className='flex items-center gap-2 py-2'>
          <span className='min-w-0 flex-1'>{clinic.name}</span>
          {action(clinic.id, clinic.steps, 'Mark done')}
        </div>
      ))}
      <div className='flex items-center gap-2 py-2'>
        <p className='min-w-0 flex-1 text-xs text-muted-foreground'>
          Mark a clinic done once you&apos;ve finished the steps above for it.
          It can&apos;t be undone.
        </p>
        {clinics.length > 1 &&
          action('all', todo.steps, 'Mark all done', 'outline')}
      </div>
    </>
  );
}
